import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Avatar, Grid, Chip, CircularProgress, Divider } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { getVendorProfile } from '../services/Api';

const Profile = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await getVendorProfile();
                if (response.success && response.data) {
                    setProfile(response.data);
                } else {
                    setError('Failed to load profile data');
                }
            } catch (err) {
                console.error("Error fetching profile", err);
                setError('An error occurred while fetching profile');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!profile) return null;

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>Vendor Profile</Typography>

            <Box sx={{ mb: 4 }}>
                {/* Profile Header Card */}
                <Card sx={{
                    maxWidth: 1000,
                    mx: 'auto',
                    borderRadius: 2,
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0',
                    overflow: 'hidden'
                }}>
                    {/* Flat Cover Banner Inside Card */}
                    <Box
                        sx={{
                            height: 120,
                            width: '100%',
                            background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                        }}
                    />

                    <CardContent sx={{ p: 3, pt: 0, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: -5, mb: 1.5 }}>
                            <Avatar
                                src={profile.image}
                                alt={profile.shopName}
                                sx={{
                                    width: 90,
                                    height: 90,
                                    bgcolor: 'background.paper',
                                    border: '3px solid white',
                                    boxShadow: 'none'
                                }}
                            >
                                {!profile.image && <StorefrontIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
                            </Avatar>
                        </Box>

                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {profile.shopName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.5, mb: 2 }}>
                            <PersonIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.7 }} />
                            Owned by {profile.ownerName}
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Chip
                                icon={profile.isActive ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                                label={profile.isActive ? "Store Active" : "Store Inactive"}
                                color={profile.isActive ? "success" : "error"}
                                variant="filled"
                                size="small"
                                sx={{ fontWeight: '500', px: 0.5 }}
                            />
                            <Chip
                                icon={profile.autoAcceptOrders ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                                label={profile.autoAcceptOrders ? "Auto-Accept On" : "Auto-Accept Off"}
                                color={profile.autoAcceptOrders ? "primary" : "default"}
                                variant={profile.autoAcceptOrders ? "filled" : "outlined"}
                                size="small"
                                sx={{ fontWeight: '500', px: 0.5 }}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <Grid container spacing={2} sx={{ maxWidth: 1000, mx: 'auto' }}>
                {/* Stats Row */}
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Card sx={{ borderRadius: 2, bgcolor: '#FFFBF2', border: '1px solid #FFE0B2', boxShadow: 'none' }}>
                                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                                    <Avatar sx={{ bgcolor: '#FFB74D', mr: 2 }}>
                                        <StarIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Rating</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#E65100' }}>
                                            {profile.rating || 'N/A'} <Typography component="span" variant="body2" sx={{ opacity: 0.7 }}>({profile.ratingCount || 0} reviews)</Typography>
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Card sx={{ borderRadius: 2, bgcolor: '#F3E5F5', border: '1px solid #E1BEE7', boxShadow: 'none' }}>
                                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                                    <Avatar sx={{ bgcolor: '#BA68C8', mr: 2 }}>
                                        <AccessTimeIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Est. Delivery Time</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#4A148C' }}>
                                            {profile.deliveryTime || 'N/A'} <Typography component="span" variant="body2" sx={{ opacity: 0.7 }}>mins</Typography>
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Card sx={{ borderRadius: 2, bgcolor: '#E8F5E9', border: '1px solid #C8E6C9', boxShadow: 'none' }}>
                                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                                    <Avatar sx={{ bgcolor: '#66BB6A', mr: 2 }}>
                                        <AccountBalanceWalletIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Wallet Balance</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1B5E20' }}>
                                            ₹{profile.walletBalance || '0.00'}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Contact and Address Section */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ borderRadius: 2, height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Contact Information</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#F5F5F5', color: '#757575', width: 40, height: 40, mr: 2 }}>
                                    <PhoneIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Mobile</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{profile.mobile || 'N/A'}</Typography>
                                </Box>
                            </Box>
                            <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ bgcolor: '#F5F5F5', color: '#757575', width: 40, height: 40, mr: 2 }}>
                                    <EmailIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Email Address</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{profile.email || 'N/A'}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ borderRadius: 2, height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Store Address</Typography>
                            {profile.addresses && profile.addresses.length > 0 ? (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <Avatar sx={{ bgcolor: '#FFEBEE', color: '#E53935', width: 40, height: 40, mr: 2 }}>
                                        <LocationOnIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            {profile.addresses[0].addressLine1}
                                        </Typography>
                                        {profile.addresses[0].addressLine2 && profile.addresses[0].addressLine2 !== 'N/A' && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                {profile.addresses[0].addressLine2}
                                            </Typography>
                                        )}
                                        <Typography variant="body2" color="text.secondary">
                                            {profile.addresses[0].city}, {profile.addresses[0].state} - {profile.addresses[0].pincode}
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                    <LocationOnIcon sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
                                    <Typography>No address provided.</Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Profile;
